import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
} from '@mui/material';

import { FlagsApi } from '@/api/api.flags';
import { Input } from '@/components/FormElements';
import { IPersonFlag, IPersonFlagDeleteRequest } from '@/models';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';
import { canUserDeleteFlag } from '@/utils';

interface PersonFlagDeleteModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  flag: IPersonFlag | null;
  personId: string;
}

interface DeleteFormData {
  deleteReason: string;
}

export const PersonFlagDeleteModal = ({
  open,
  onClose,
  onConfirm,
  flag,
  personId,
}: PersonFlagDeleteModalProps) => {
  const { updateToast } = useToastStore();
  const { user } = useUserStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DeleteFormData>({
    defaultValues: {
      deleteReason: '',
    },
  });

  const onSubmit = async (data: DeleteFormData) => {
    if (!flag) return;

    if (!canUserDeleteFlag(user)) {
      updateToast({
        open: true,
        message: 'You do not have permission to delete flags',
        type: ConfirmTypes.ERROR,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const request: IPersonFlagDeleteRequest = {
        deleteReason: data.deleteReason,
      };

      await FlagsApi.removePersonFlag(personId, flag._id, request);

      updateToast({
        open: true,
        message: 'Flag removed successfully',
        type: ConfirmTypes.SUCCESS,
      });

      handleClose();
      onConfirm();
    } catch (error: any) {
      updateToast({
        open: true,
        message: error.message || 'Failed to remove flag',
        type: ConfirmTypes.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset({ deleteReason: '' });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Are you sure you want to delete the {flag?.flag.name} flag?
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              This action cannot be undone. Please provide a reason for removing
              this flag.
            </Typography>
          </Box>

          <Controller
            name="deleteReason"
            control={control}
            rules={{
              required: 'Please provide a reason for deletion',
              minLength: {
                value: 3,
                message: 'Reason must be at least 3 characters long',
              },
            }}
            render={({ field }) => (
              <Input
                {...field}
                label="Reason for deletion *"
                multiline
                rows={3}
                error={errors.deleteReason?.message}
                placeholder="Enter the reason for removing this flag..."
                autoFocus
              />
            )}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Removing...' : 'Delete'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
